import React, { useState } from 'react';
import { Edit2, Info, HeartPulse, Sparkles, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
const pulseScoreData = [{
  month: 'Oct',
  score: 68,
  isKeyEvent: false
}, {
  month: 'Nov',
  score: 72,
  isKeyEvent: false
}, {
  month: 'Dec',
  score: 75,
  isKeyEvent: false
}, {
  month: 'Jan',
  score: 78,
  event: 'Private financials show strong revenue growth',
  isKeyEvent: true
}, {
  month: 'Feb',
  score: 80,
  isKeyEvent: false
}, {
  month: 'Mar',
  score: 82,
  isKeyEvent: false
}];
const healthScoreData = [{
  month: 'Oct',
  score: 86
}, {
  month: 'Nov',
  score: 87,
  event: 'Positive trade references received',
  isKeyEvent: true
}, {
  month: 'Dec',
  score: 88
}, {
  month: 'Jan',
  score: 90,
  event: 'Strong financials and low debt',
  isKeyEvent: true
}, {
  month: 'Feb',
  score: 89
}, {
  month: 'Mar',
  score: 88
}];
const paymentScoreData = [{
  month: 'Oct',
  score: 72
}, {
  month: 'Nov',
  score: 70
}, {
  month: 'Dec',
  score: 68,
  event: 'Seasonal payment slowdown',
  isKeyEvent: true
}, {
  month: 'Jan',
  score: 67
}, {
  month: 'Feb',
  score: 69,
  event: 'DBT increased slightly',
  isKeyEvent: true
}, {
  month: 'Mar',
  score: 68
}];
function PositiveIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-2815">
      <div className="w-1 h-1.5 bg-green-500 rounded-sm" data-id="element-2816" />
      <div className="w-1 h-2.5 bg-green-500 rounded-sm" data-id="element-2817" />
      <div className="w-1 h-3.5 bg-green-500 rounded-sm" data-id="element-2818" />
    </div>;
}
function NegativeIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-2819">
      <div className="w-1 h-1.5 bg-red-500 rounded-sm" data-id="element-2820" />
      <div className="w-1 h-2.5 bg-red-500 rounded-sm" data-id="element-2821" />
      <div className="w-1 h-3.5 bg-red-500 rounded-sm" data-id="element-2822" />
    </div>;
}
function ScoreTooltip({
  active,
  payload,
  label,
  color
}: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const dotColor = data.score !== undefined ? getDotColor(data.score) : color;
    return <div className="bg-white rounded-lg border border-gray-200 shadow-lg px-4 py-3 max-w-[250px]" data-id="element-2823">
        <div className="flex items-center justify-between mb-2" data-id="element-2824">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2825">
            {label}
          </p>
          <div className="flex items-center gap-1.5" data-id="element-2826">
            <div className="w-2 h-2 rounded-full" style={{
            backgroundColor: dotColor
          }} data-id="element-2827" />
            <span className="text-sm font-bold text-gray-900" data-id="element-2828">
              {payload[0].value}
            </span>
          </div>
        </div>
        {data.event && <p className="text-sm text-gray-700 leading-snug border-t border-gray-100 pt-2 mt-1" data-id="element-2829">
            {data.event}
          </p>}
      </div>;
  }
  return null;
}
function getDotColor(score: number) {
  if (score <= 30) return '#ef4444';
  if (score <= 70) return '#eab308';
  return '#22c55e';
}
const CustomDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  if (payload.isKeyEvent) {
    return <g data-id="element-2830">
        <circle cx={cx} cy={cy} r={12} fill={color} fillOpacity={0.2} className="animate-pulse" data-id="element-2831" />
        <circle cx={cx} cy={cy} r={6} fill="#ffffff" stroke={color} strokeWidth={3} data-id="element-2832" />
      </g>;
  }
  return <circle cx={cx} cy={cy} r={3} fill={color} stroke={color} strokeWidth={2} data-id="element-2833" />;
};
const CustomActiveDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  return <circle cx={cx} cy={cy} r={8} fill={color} stroke="#fff" strokeWidth={2} data-id="element-2834" />;
};
export function OverviewTab() {
  const [healthExpanded, setHealthExpanded] = useState(true);
  const [paymentExpanded, setPaymentExpanded] = useState(true);
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2835">
      {/* Top Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2836">
        {/* Credit Settings */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-1" data-id="element-2837">
          <div className="flex justify-between items-center mb-6" data-id="element-2838">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-2839">Credit Settings</h3>
            <button className="w-8 h-8 bg-indigo-600 hover:bg-indigo-700 rounded-full flex items-center justify-center text-white transition-colors" data-id="element-2840">
              <Edit2 className="w-4 h-4" data-id="element-2841" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-y-3 gap-x-4" data-id="element-2842">
            <div data-id="element-2843">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2844">
                Credit Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-2845">$50,000</p>
            </div>
            <div data-id="element-2846">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2847">
                Recommended Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-2848">$50,000</p>
            </div>
            <div data-id="element-2849">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2850">
                Outstanding Balance
              </p>
              <p className="text-sm text-gray-600" data-id="element-2851">$12,450</p>
            </div>
            <div data-id="element-2852">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2853">
                AP Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-2854">Angela Martin</p>
            </div>
            <div data-id="element-2855">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2856">
                Terms
              </p>
              <p className="text-sm text-gray-600" data-id="element-2857">Net 30</p>
            </div>
            <div data-id="element-2858">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2859">
                Purchasing Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-2860">Dwight Schrute</p>
            </div>
            <div data-id="element-2861">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2862">
                Late Pay Penalty
              </p>
              <p className="text-sm text-gray-600" data-id="element-2863">1.5%</p>
            </div>
            <div data-id="element-2864">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2865">
                Company Officer
              </p>
              <p className="text-sm text-gray-600" data-id="element-2866">Michael Scott</p>
            </div>
            <div data-id="element-2867">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2868">
                Date Last Approved
              </p>
              <p className="text-sm text-gray-600" data-id="element-2869">2/20/2026</p>
            </div>
            <div data-id="element-2870">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2871">
                Group
              </p>
              <p className="text-sm text-gray-600" data-id="element-2872">Wholesaler</p>
            </div>
          </div>
        </div>

        {/* Pulse Score */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-2 flex flex-col" data-id="element-2873">
          <div className="flex items-center gap-2 mb-4" data-id="element-2874">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2875">Pulse Score</h3>
          </div>
          <div className="flex gap-6 mb-6" data-id="element-2876">
            <div className="bg-green-100 rounded-lg p-4 flex flex-col items-center justify-center min-w-[120px] shrink-0" data-id="element-2877">
              <span className="text-4xl font-bold text-gray-900" data-id="element-2878">72</span>
              <span className="text-sm font-medium text-gray-800 mt-1" data-id="element-2879">
                Low Risk
              </span>
            </div>
            <p className="text-gray-600 text-base leading-relaxed pt-2" data-id="element-2880">
              Pulse Score of 72 indicates low risk. Score has shown volatility
              over the past year but recently stabilized in the healthy range.
            </p>
          </div>
          <div className="flex-1 min-h-[200px] relative" data-id="element-2881">
            <div className="absolute top-0 right-0 flex items-center gap-2 text-xs text-gray-500 z-10" data-id="element-2882">
              <div className="w-3 h-3 bg-black rounded-sm" data-id="element-2883"></div>
              Pulse Score
            </div>
            <ResponsiveContainer width="100%" height="100%" data-id="element-2884">
              <LineChart data={pulseScoreData} margin={{
              top: 20,
              right: 10,
              left: -20,
              bottom: 10
            }} data-id="element-2885">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2886" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} dy={10} data-id="element-2887" />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} data-id="element-2888" />
                <Tooltip content={<ScoreTooltip data-id="element-2890" />} cursor={{
                stroke: '#E5E7EB',
                strokeWidth: 2,
                strokeDasharray: '4 4'
              }} data-id="element-2889" />
                <ReferenceLine y={70} stroke="#22c55e" strokeDasharray="4 4" data-id="element-2891" />
                <ReferenceLine y={30} stroke="#ef4444" strokeDasharray="4 4" data-id="element-2892" />
                <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-2894" />} activeDot={<CustomActiveDot data-id="element-2895" />} data-id="element-2893" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Health Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2896">
        <button onClick={() => setHealthExpanded(!healthExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-2897">
          <div className="flex items-center gap-3" data-id="element-2898">
            <HeartPulse className="w-5 h-5 text-gray-700" data-id="element-2899" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2900">Health Score</h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-green-500 bg-white ml-2" data-id="element-2901">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" data-id="element-2902"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-2903">88</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-2904" />
          </div>
          {healthExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-2905" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-2906" />}
        </button>

        {healthExpanded && <div className="p-5" data-id="element-2907">
            <p className="text-base text-gray-600 mb-6" data-id="element-2908">
              DUNDER MIFFLIN PAPER COMPANY has a 1.2% probability of insolvency
              or bankruptcy in the next 12 months.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2909">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2910">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2911">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2912">
                  <div className="flex items-start gap-2" data-id="element-2913">
                    <NegativeIndicator data-id="element-2914" />
                    <span className="text-sm text-gray-600" data-id="element-2915">
                      Recent DBT increase trend (67% MoM)
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2916">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2917">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2918">
                  <div className="flex items-start gap-2" data-id="element-2919">
                    <PositiveIndicator data-id="element-2920" />
                    <span className="text-sm text-gray-600" data-id="element-2921">
                      Strong financials (77 years in business, solid Z-Score)
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-2922">
                    <PositiveIndicator data-id="element-2923" />
                    <span className="text-sm text-gray-600" data-id="element-2924">
                      Stable revenue and employee growth
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-2925">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2926">
                  <LineChart data={healthScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-2927">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2928" />
                    <XAxis dataKey="month" hide data-id="element-2929" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-2930" />
                    <Tooltip content={<ScoreTooltip color="#22c55e" data-id="element-2932" />} data-id="element-2931" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-2934" />} activeDot={<CustomActiveDot data-id="element-2935" />} data-id="element-2933" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2936">
        <button onClick={() => setPaymentExpanded(!paymentExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-2937">
          <div className="flex items-center gap-3" data-id="element-2938">
            <CreditCard className="w-5 h-5 text-gray-700" data-id="element-2939" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2940">Payment Score</h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-yellow-500 bg-white ml-2" data-id="element-2941">
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" data-id="element-2942"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-2943">68</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-2944" />
          </div>
          {paymentExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-2945" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-2946" />}
        </button>

        {paymentExpanded && <div className="p-5" data-id="element-2947">
            <p className="text-base text-gray-600 mb-6" data-id="element-2948">
              DUNDER MIFFLIN PAPER COMPANY has a 12.3% probability of late
              payment, indicating moderate risk.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2949">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2950">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2951">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2952">
                  <div className="flex items-start gap-2" data-id="element-2953">
                    <NegativeIndicator data-id="element-2954" />
                    <span className="text-sm text-gray-600" data-id="element-2955">
                      Average 60+ day overdue balance ratio
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-2956">
                    <NegativeIndicator data-id="element-2957" />
                    <span className="text-sm text-gray-600" data-id="element-2958">
                      Recent DBT increase (6 to 10 days)
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2959">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2960">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2961">
                  <div className="flex items-start gap-2" data-id="element-2962">
                    <PositiveIndicator data-id="element-2963" />
                    <span className="text-sm text-gray-600" data-id="element-2964">
                      DBT well below industry average
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-2965">
                    <PositiveIndicator data-id="element-2966" />
                    <span className="text-sm text-gray-600" data-id="element-2967">
                      Your AR consistently 2-4 days
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-2968">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2969">
                  <LineChart data={paymentScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-2970">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2971" />
                    <XAxis dataKey="month" hide data-id="element-2972" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-2973" />
                    <Tooltip content={<ScoreTooltip color="#eab308" data-id="element-2975" />} data-id="element-2974" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-2977" />} activeDot={<CustomActiveDot data-id="element-2978" />} data-id="element-2976" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}